import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { drawMandelbrotFractal, applyPanAndRenderEdges } from './fractal_math';
import { getWebGLContext, drawMandelbrotFractalWebGL } from './fractalWebGL';
import { useFractalWorker, serializeColorFunc } from './useFractalWorker';

/** Interactive canvas that draws Mandelbrot set fractals */
export function FractalCanvas({
  width,
  height,
  xRange: xRangeInit,
  yRange: yRangeInit,
  colorFunc,
  // 'webgl' (prefer WebGL when available) or 'canvas' (force 2D/worker)
  preferredRenderer = 'webgl',
  // Optional: show renderer info label (only used for the first canvas)
  showRendererToggle = false,
}) {
  const [xRange, setXrange] = useState(xRangeInit);
  const [yRange, setYrange] = useState(yRangeInit);
  const [history, setHistory] = useState([]);
  
  // Track previous ranges for pan optimization
  const prevRangesRef = useRef({ xRange: xRangeInit, yRange: yRangeInit });
  const [isPan, setIsPan] = useState(false);
  const [isZoom, setIsZoom] = useState(false);
  
  // Refs for wheel handler to access current state
  const xRangeRef = useRef(xRange);
  const yRangeRef = useRef(yRange);
  xRangeRef.current = xRange;
  yRangeRef.current = yRange;

  const [renderBackend, setRenderBackend] = useState(null); // 'WebGL' or 'Canvas'

  const [actualWidth, setActualWidth] = useState();
  const [actualHeight, setActualHeight] = useState();
  
  // Web Worker for off-main-thread rendering (with smart fallback)
  const { renderFull, renderPan, shouldUseWorker } = useFractalWorker();
  
  // Serialize colorFunc for worker (memoized to avoid re-parsing)
  const colorParams = useMemo(() => serializeColorFunc(colorFunc), [colorFunc]);
  const colorParamsRef = useRef(colorParams);
  colorParamsRef.current = colorParams;

  const [mousePosX, setMousePosX] = useState();
  const [mousePosY, setMousePosY] = useState();
  const [mouseDownPos, setMouseDownPos] = useState([null, null]);
  const [isShiftPressed, setIsShiftPressed] = useState(false);

  const [isRendered, setIsRendered] = useState(false);

  const canvasElRef = useRef();
  // WebGL real-time pan: range and position at mousedown; null when not dragging
  const panStartRef = useRef(null);
  const rafPanRef = useRef(null);
  const historySavedForPanRef = useRef(false);
  const touchPanStartRef = useRef(null);
  const rafTouchPanRef = useRef(null);
  const saveHistoryRef = useRef(null);
  const rafPinchRef = useRef(null);
  const pinchPendingRef = useRef(null);

  // track the actual width and height of the canvas DOM element
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    setActualWidth(canvasEl.offsetWidth);
    setActualHeight(canvasEl.offsetHeight);
  }, [canvasElRef]);

  // when re-rendering, clear the canvas so the previous render doesn't show through
  // BUT: skip clearing for pan/zoom operations to avoid flashing
  useEffect(() => {
    if (!isRendered && !isPan && !isZoom) {
      const canvasEl = canvasElRef.current;
      canvasEl.width = canvasEl.width;   // eslint-disable-line no-self-assign
      canvasEl.height = canvasEl.height; // eslint-disable-line no-self-assign
    }
  }, [canvasElRef, isRendered, isPan, isZoom]);

  // listen for a "render" event, unlisten, then render the canvas
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    let cancelled = false;

    const renderListener = async () => {
      canvasEl.removeEventListener('render', renderListener);
      if (isRendered || cancelled) return;

      // Decide render path once per canvas: WebGL when preferred & available, else 2D/worker.
      if (canvasEl._webGLDecided === undefined) {
        if (preferredRenderer === 'webgl') {
          const glCtx = getWebGLContext(canvasEl);
          canvasEl._webGLDecided = true;
          canvasEl._useWebGL = !!glCtx;
        } else {
          canvasEl._webGLDecided = true;
          canvasEl._useWebGL = false;
        }
        if (!cancelled) {
          setRenderBackend(canvasEl._useWebGL ? 'WebGL' : 'Canvas');
        }
      }

      if (canvasEl._useWebGL) {
        const ok = drawMandelbrotFractalWebGL(canvasEl, xRange, yRange, colorParams);
        if (ok) {
          prevRangesRef.current = { xRange, yRange };
          setIsRendered(true);
          setIsPan(false);
          setIsZoom(false);
          return;
        }
        canvasEl._useWebGL = false;
        if (!cancelled) {
          setRenderBackend('Canvas');
        }
      }

      const context = canvasEl.getContext('2d');
      if (!context) return;

      const prevRanges = prevRangesRef.current;
      const useWorker = shouldUseWorker(canvasEl.width, canvasEl.height);

      // Try pan optimization if this is a pan operation
      if (isPan) {
        if (process.env.NODE_ENV !== 'production') {
          console.log(`attempting pan-optimized render (worker: ${useWorker})...`);
        }
        if (useWorker) {
          try {
            // Get existing canvas data before worker processes
            const existingData = context.getImageData(0, 0, canvasEl.width, canvasEl.height);
            
            const result = await renderPan({
              canvasWidth: canvasEl.width,
              canvasHeight: canvasEl.height,
              oldXRange: prevRanges.xRange,
              oldYRange: prevRanges.yRange,
              newXRange: xRange,
              newYRange: yRange,
              colorParams
            });
            
            if (cancelled) return;
            
            const { pixelOffsetX, pixelOffsetY, regions } = result;
            
            // Shift existing pixels
            const newImageData = context.createImageData(canvasEl.width, canvasEl.height);
            const oldPixels = existingData.data;
            const newPixels = newImageData.data;
            
            for (let y = 0; y < canvasEl.height; y++) {
              const srcY = y + pixelOffsetY;
              if (srcY < 0 || srcY >= canvasEl.height) continue;
              
              for (let x = 0; x < canvasEl.width; x++) {
                const srcX = x + pixelOffsetX;
                if (srcX < 0 || srcX >= canvasEl.width) continue;
                
                const srcIdx = (srcY * canvasEl.width + srcX) * 4;
                const dstIdx = (y * canvasEl.width + x) * 4;
                
                newPixels[dstIdx] = oldPixels[srcIdx];
                newPixels[dstIdx + 1] = oldPixels[srcIdx + 1];
                newPixels[dstIdx + 2] = oldPixels[srcIdx + 2];
                newPixels[dstIdx + 3] = oldPixels[srcIdx + 3];
              }
            }
            
            // Apply regions from worker (bulk copy per row)
            for (const region of regions) {
              const regionData = region.pixels;
              const rowBytes = region.width * 4;
              for (let ry = 0; ry < region.height; ry++) {
                const srcStart = ry * rowBytes;
                const dstStart = ((region.y + ry) * canvasEl.width + region.x) * 4;
                newPixels.set(regionData.subarray(srcStart, srcStart + rowBytes), dstStart);
              }
            }
            
            context.putImageData(newImageData, 0, 0);
          } catch (err) {
            console.warn('Worker pan failed, falling back to main thread:', err);
            applyPanAndRenderEdges(canvasEl, prevRanges.xRange, prevRanges.yRange, xRange, yRange, colorFunc);
          }
        } else {
          // Main thread fallback
          const success = applyPanAndRenderEdges(
            canvasEl,
            prevRanges.xRange,
            prevRanges.yRange,
            xRange,
            yRange,
            colorFunc
          );
          
          if (!success) {
            if (process.env.NODE_ENV !== 'production') {
              console.log('pan optimization failed, falling back to full render...');
            }
            drawMandelbrotFractal(canvasEl, xRange, yRange, colorFunc);
          }
        }
        
        prevRangesRef.current = { xRange, yRange };
        setIsRendered(true);
        setIsPan(false);
        setIsZoom(false);
      } else {
        // Full render
        if (process.env.NODE_ENV !== 'production') {
          console.log(`full render starting (worker: ${useWorker})...`);
        }
        if (useWorker) {
          try {
            const pixels = await renderFull({
              canvasWidth: canvasEl.width,
              canvasHeight: canvasEl.height,
              xRange,
              yRange,
              colorParams
            });
            
            if (cancelled) return;
            
            // Use pixels buffer directly (already Uint8ClampedArray from worker combine)
            context.putImageData(new ImageData(pixels, canvasEl.width, canvasEl.height), 0, 0);
          } catch (err) {
            console.warn('Worker render failed, falling back to main thread:', err);
            drawMandelbrotFractal(canvasEl, xRange, yRange, colorFunc);
          }
        } else {
          // Main thread fallback
          drawMandelbrotFractal(canvasEl, xRange, yRange, colorFunc);
        }
        
        prevRangesRef.current = { xRange, yRange };
        setIsRendered(true);
        setIsZoom(false);
      }
    };
    
    canvasEl.addEventListener('render', renderListener);
    
    return () => {
      cancelled = true;
      canvasEl.removeEventListener('render', renderListener);
    };
  }, [canvasElRef, isRendered, xRange, yRange, colorFunc, colorParams, isPan, shouldUseWorker, renderFull, renderPan, preferredRenderer]);

  // for calculating mouse (x, y) positions over the canvas relative to actual size
  const mouseXY = (event) => {
    // hack: parentNode for .image-container offsetLeft and offsetTop
    const X = (event.pageX - event.target.parentNode.offsetLeft);
    const Y = (event.pageY - event.target.parentNode.offsetTop);
    // console.log(`(${X}, ${Y})`);
    const Xscaled = X / actualWidth;
    const Yscaled = Y / actualHeight;
    const mouseX = xRange[0]*(1 - Xscaled) + xRange[1]*Xscaled;
    const mouseY = yRange[0]*(1 - Yscaled) + yRange[1]*Yscaled;
    return [mouseX, mouseY];
  }

  // Maximum history size to prevent memory issues (ImageData can be large)
  const MAX_HISTORY_SIZE = 20;
  
  // save current ranges and canvas image to history before making a change
  const saveHistory = useCallback(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return;
    let imageData = null;
    // For 2D/worker path, capture ImageData for instant undo.
    // For WebGL, we can't use getImageData on a webgl canvas; rely on re-render instead.
    if (!canvasEl._useWebGL) {
      const context = canvasEl.getContext('2d');
      if (context) {
        imageData = context.getImageData(0, 0, canvasEl.width, canvasEl.height);
      }
    }
    setHistory(prev => {
      const newHistory = [...prev, { xRange, yRange, imageData }];
      // Limit history size to prevent memory bloat
      if (newHistory.length > MAX_HISTORY_SIZE) {
        return newHistory.slice(-MAX_HISTORY_SIZE);
      }
      return newHistory;
    });
  }, [xRange, yRange]);
  saveHistoryRef.current = saveHistory;

  // undo: restore previous ranges and canvas image from history
  const undo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setXrange(prev.xRange);
    setYrange(prev.yRange);
    
    // Update prevRangesRef to match restored state (critical for pan optimization)
    prevRangesRef.current = { xRange: prev.xRange, yRange: prev.yRange };
    
    // Restore canvas image directly if available (instant, no recomputation)
    if (prev.imageData) {
      const canvasEl = canvasElRef.current;
      if (canvasEl) {
        const context = canvasEl.getContext('2d');
        context.putImageData(prev.imageData, 0, 0);
        setIsRendered(true); // Already rendered!
        return;
      }
    }
    // Fallback to re-render if no imageData
    setIsRendered(false);
  }, [history]);

  // listen for Cmd+Z / Ctrl+Z to undo, and track shift key for zoom cursor
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    
    const handleKeyDown = (event) => {
      // Track shift key for zoom cursor
      if (event.key === 'Shift') {
        setIsShiftPressed(true);
      }
      // Undo with Cmd+Z / Ctrl+Z when hovering over canvas
      if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
        if (canvasEl.matches(':hover')) {
          event.preventDefault();
          undo();
        }
      }
    };
    
    const handleKeyUp = (event) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };
    
    // Also reset shift state if window loses focus
    const handleBlur = () => {
      setIsShiftPressed(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [undo]);

  // Handle shift+wheel zoom with scroll prevention
  // All wheel handling is done here with native listener for full event control.
  // Apply zoom incrementally per wheel event for smooth behavior.
  const zoomTimeoutRef = useRef(null);
  const pendingZoomRef = useRef({ hasHistory: false });
  const pinchRef = useRef(null);

  // Refs for actual dimensions (needed in wheel handler)
  const actualWidthRef = useRef(actualWidth);
  const actualHeightRef = useRef(actualHeight);
  actualWidthRef.current = actualWidth;
  actualHeightRef.current = actualHeight;
  
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    const containerEl = canvasEl.parentElement; // .image-container
    
    const handleWheel = (event) => {
      // Zoom: shift+scroll (mouse) or trackpad pinch (macOS sends wheel with ctrlKey)
      const isZoomGesture = event.shiftKey || event.ctrlKey;
      if (!isZoomGesture) return;

      // Check if event is over the canvas or its container
      const isOverCanvas = event.target === canvasEl || 
                          (containerEl && containerEl.contains(event.target));
      
      if (!isOverCanvas) return;
      
      // Prevent scroll!
      event.preventDefault();
      event.stopPropagation();
      
      // Calculate mouse position in fractal coordinates (zoom center point)
      const rect = containerEl.getBoundingClientRect();
      const pixelX = event.clientX - rect.left;
      const pixelY = event.clientY - rect.top;
      const currentXRange = xRangeRef.current;
      const currentYRange = yRangeRef.current;
      const w = actualWidthRef.current || canvasEl.offsetWidth;
      const h = actualHeightRef.current || canvasEl.offsetHeight;
      const mouseX = currentXRange[0] + (pixelX / w) * (currentXRange[1] - currentXRange[0]);
      const mouseY = currentYRange[0] + (pixelY / h) * (currentYRange[1] - currentYRange[0]);
      
      // On macOS, shift+scroll converts to horizontal scroll (deltaX).
      // Use whichever delta is larger.
      const delta = Math.abs(event.deltaY) > Math.abs(event.deltaX) 
        ? event.deltaY 
        : event.deltaX;
      
      // Save history only once per zoom gesture
      if (!pendingZoomRef.current.hasHistory) {
        let imageData = null;
        // For WebGL canvases we can't rely on getImageData; undo will re-render instead.
        if (!canvasEl._useWebGL) {
          const context = canvasEl.getContext('2d');
          if (context) {
            imageData = context.getImageData(0, 0, canvasEl.width, canvasEl.height);
          }
        }
        setHistory(prev => {
          const newHistory = [...prev, { 
            xRange: currentXRange, 
            yRange: currentYRange,
            imageData 
          }];
          return newHistory.length > 20 ? newHistory.slice(-20) : newHistory;
        });
        pendingZoomRef.current.hasHistory = true;
      }
      
      // Use a timeout only to mark the end of a zoom gesture (for history),
      // but apply zoom immediately per wheel event.
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
      
      zoomTimeoutRef.current = setTimeout(() => {
        // Gesture ended; next wheel movement will save a new history entry.
        pendingZoomRef.current.hasHistory = false;
      }, 150);

      // Per-event zoom factor: small exponential scaling for smooth behavior.
      // Trackpad pinch (ctrlKey): flip sign so pinch-together = zoom out, pinch-apart = zoom in.
      const intensity = 0.008;
      const zoomSign = event.ctrlKey ? 1 : -1;
      const zoomAmount = Math.max(-0.6, Math.min(0.6, zoomSign * delta * intensity));
      const scaleFactor = Math.exp(zoomAmount); // >1 out, <1 in

      const xr = xRangeRef.current;
      const yr = yRangeRef.current;

      const newXRange = [
        mouseX - (mouseX - xr[0]) * scaleFactor,
        mouseX + (xr[1] - mouseX) * scaleFactor
      ];
      const newYRange = [
        mouseY - (mouseY - yr[0]) * scaleFactor,
        mouseY + (yr[1] - mouseY) * scaleFactor
      ];

      setXrange(newXRange);
      setYrange(newYRange);

      if (canvasEl._useWebGL) {
        // Instant WebGL redraw at full canvas resolution.
        prevRangesRef.current = { xRange: newXRange, yRange: newYRange };
        drawMandelbrotFractalWebGL(canvasEl, newXRange, newYRange, colorParamsRef.current);
        setIsRendered(true);
      } else {
        // 2D/worker path: mark as needing re-render on next 'render' event
        setIsZoom(true);
        setIsRendered(false);
      }
    };

    // Attach directly to canvas with passive: false
    canvasEl.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      canvasEl.removeEventListener('wheel', handleWheel, { passive: false });
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []); // Empty deps - uses refs for current state

  // Touch: single-finger pan + two-finger pinch-zoom. Native listeners with passive: false.
  useEffect(() => {
    const canvasEl = canvasElRef.current;
    if (!canvasEl) return;

    const containerEl = canvasEl.parentElement;
    const pixelToFractal = (clientX, clientY) => {
      if (!containerEl) return [0, 0];
      const rect = containerEl.getBoundingClientRect();
      const w = actualWidthRef.current || canvasEl.offsetWidth;
      const h = actualHeightRef.current || canvasEl.offsetHeight;
      const xr = xRangeRef.current;
      const yr = yRangeRef.current;
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const fx = xr[0] + (px / w) * (xr[1] - xr[0]);
      const fy = yr[0] + (py / h) * (yr[1] - yr[0]);
      return [fx, fy];
    };

    const handleTouchStart = (event) => {
      if (event.touches.length === 2) {
        touchPanStartRef.current = null;
        event.preventDefault();
        if (!containerEl) return;
        const rect = containerEl.getBoundingClientRect();
        const t0 = event.touches[0];
        const t1 = event.touches[1];
        const x0 = t0.clientX - rect.left;
        const y0 = t0.clientY - rect.top;
        const x1 = t1.clientX - rect.left;
        const y1 = t1.clientY - rect.top;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const startDistance = Math.hypot(dx, dy) || 1;
        const centerPixelX = (x0 + x1) * 0.5;
        const centerPixelY = (y0 + y1) * 0.5;
        const currentXRange = xRangeRef.current;
        const currentYRange = yRangeRef.current;
        const w = actualWidthRef.current || canvasEl.offsetWidth;
        const h = actualHeightRef.current || canvasEl.offsetHeight;
        const centerX = currentXRange[0] + (centerPixelX / w) * (currentXRange[1] - currentXRange[0]);
        const centerY = currentYRange[0] + (centerPixelY / h) * (currentYRange[1] - currentYRange[0]);
        if (!pendingZoomRef.current.hasHistory) {
          let imageData = null;
          if (!canvasEl._useWebGL) {
            const context = canvasEl.getContext('2d');
            if (context) imageData = context.getImageData(0, 0, canvasEl.width, canvasEl.height);
          }
          setHistory(prev => {
            const newHistory = [...prev, { xRange: currentXRange, yRange: currentYRange, imageData }];
            return newHistory.length > 20 ? newHistory.slice(-20) : newHistory;
          });
          pendingZoomRef.current.hasHistory = true;
        }
        pinchRef.current = {
          startDistance, centerX, centerY,
          startXRange: [currentXRange[0], currentXRange[1]],
          startYRange: [currentYRange[0], currentYRange[1]],
        };
        return;
      }
      if (event.touches.length === 1) {
        event.preventDefault();
        const [fx, fy] = pixelToFractal(event.touches[0].clientX, event.touches[0].clientY);
        const xr = xRangeRef.current;
        const yr = yRangeRef.current;
        touchPanStartRef.current = { x: fx, y: fy, xRange: [xr[0], xr[1]], yRange: [yr[0], yr[1]] };
        historySavedForPanRef.current = false;
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 2) {
        const pinch = pinchRef.current;
        if (!pinch) return;
        event.preventDefault();
        if (!containerEl) return;
        const rect = containerEl.getBoundingClientRect();
        const t0 = event.touches[0];
        const t1 = event.touches[1];
        const x0 = t0.clientX - rect.left;
        const y0 = t0.clientY - rect.top;
        const x1 = t1.clientX - rect.left;
        const y1 = t1.clientY - rect.top;
        const dx = x1 - x0;
        const dy = y1 - y0;
        const currentDistance = Math.hypot(dx, dy) || 1;
        const rawScale = pinch.startDistance / currentDistance;
        const clamped = Math.max(0.25, Math.min(4.0, rawScale));
        const xr = pinch.startXRange;
        const yr = pinch.startYRange;
        const cx = pinch.centerX;
        const cy = pinch.centerY;
        const newXRange = [cx - (cx - xr[0]) * clamped, cx + (xr[1] - cx) * clamped];
        const newYRange = [cy - (cy - yr[0]) * clamped, cy + (yr[1] - cy) * clamped];
        prevRangesRef.current = { xRange: newXRange, yRange: newYRange };
        if (canvasEl._useWebGL) {
          drawMandelbrotFractalWebGL(canvasEl, newXRange, newYRange, colorParamsRef.current);
        } else {
          pinchPendingRef.current = { xRange: newXRange, yRange: newYRange };
          if (rafPinchRef.current == null) {
            rafPinchRef.current = requestAnimationFrame(() => {
              rafPinchRef.current = null;
              const p = pinchPendingRef.current;
              if (!p) return;
              prevRangesRef.current = { xRange: p.xRange, yRange: p.yRange };
              setXrange(p.xRange);
              setYrange(p.yRange);
              setIsZoom(true);
              setIsRendered(false);
            });
          }
        }
        return;
      }
      if (event.touches.length === 1 && touchPanStartRef.current) {
        event.preventDefault();
        const start = touchPanStartRef.current;
        const [currentX, currentY] = pixelToFractal(event.touches[0].clientX, event.touches[0].clientY);
        const deltaX = start.x - currentX;
        const deltaY = start.y - currentY;
        const newXRange = [start.xRange[0] + deltaX, start.xRange[1] + deltaX];
        const newYRange = [start.yRange[0] + deltaY, start.yRange[1] + deltaY];
        prevRangesRef.current = { xRange: newXRange, yRange: newYRange };
        if (canvasEl._useWebGL) {
          canvasEl._pendingPanRange = { xRange: newXRange, yRange: newYRange };
          if (rafTouchPanRef.current != null) return;
          rafTouchPanRef.current = requestAnimationFrame(() => {
            rafTouchPanRef.current = null;
            if (!historySavedForPanRef.current) {
              saveHistoryRef.current?.();
              historySavedForPanRef.current = true;
            }
            const pending = canvasEl._pendingPanRange;
            if (!pending) return;
            prevRangesRef.current = { xRange: pending.xRange, yRange: pending.yRange };
            drawMandelbrotFractalWebGL(canvasEl, pending.xRange, pending.yRange, colorParamsRef.current);
          });
        } else {
          if (!historySavedForPanRef.current) {
            saveHistoryRef.current?.();
            historySavedForPanRef.current = true;
          }
        }
      }
    };

    const handleTouchEnd = (event) => {
      if (event.touches.length === 0) {
        if (touchPanStartRef.current) {
          const { xRange: xr, yRange: yr } = prevRangesRef.current;
          setXrange(xr);
          setYrange(yr);
          if (!canvasEl._useWebGL) {
            setIsPan(true);
            setIsRendered(false);
          }
          touchPanStartRef.current = null;
        }
        historySavedForPanRef.current = false;
        if (rafTouchPanRef.current != null) {
          cancelAnimationFrame(rafTouchPanRef.current);
          rafTouchPanRef.current = null;
        }
      }
      if (event.touches.length < 2) {
        if (pinchRef.current) {
          const { xRange: xr, yRange: yr } = prevRangesRef.current;
          setXrange(xr);
          setYrange(yr);
          if (!canvasEl._useWebGL) setIsRendered(false);
          pinchRef.current = null;
        }
        pendingZoomRef.current.hasHistory = false;
        if (rafPinchRef.current != null) {
          cancelAnimationFrame(rafPinchRef.current);
          rafPinchRef.current = null;
        }
      }
    };

    const opts = { passive: false };
    canvasEl.addEventListener('touchstart', handleTouchStart, opts);
    canvasEl.addEventListener('touchmove', handleTouchMove, opts);
    canvasEl.addEventListener('touchend', handleTouchEnd, opts);
    canvasEl.addEventListener('touchcancel', handleTouchEnd, opts);

    return () => {
      canvasEl.removeEventListener('touchstart', handleTouchStart, opts);
      canvasEl.removeEventListener('touchmove', handleTouchMove, opts);
      canvasEl.removeEventListener('touchend', handleTouchEnd, opts);
      canvasEl.removeEventListener('touchcancel', handleTouchEnd, opts);
      if (rafTouchPanRef.current != null) {
        cancelAnimationFrame(rafTouchPanRef.current);
        rafTouchPanRef.current = null;
      }
      if (rafPinchRef.current != null) {
        cancelAnimationFrame(rafPinchRef.current);
        rafPinchRef.current = null;
      }
    };
  }, []);

  return <figure>
    <div className="image-container">
      <canvas ref={canvasElRef}
        width={width}
        height={height}
        onDoubleClick={(event) => {
          const canvasEl = canvasElRef.current;
          if (!canvasEl) return;
          const containerEl = canvasEl.parentElement;
          if (!containerEl) return;

          // Zoom in around the double-clicked point (like Google Maps).
          const rect = containerEl.getBoundingClientRect();
          const pixelX = event.clientX - rect.left;
          const pixelY = event.clientY - rect.top;
          const currentXRange = xRangeRef.current;
          const currentYRange = yRangeRef.current;
          const w = actualWidthRef.current || canvasEl.offsetWidth;
          const h = actualHeightRef.current || canvasEl.offsetHeight;
          const centerX = currentXRange[0] + (pixelX / w) * (currentXRange[1] - currentXRange[0]);
          const centerY = currentYRange[0] + (pixelY / h) * (currentYRange[1] - currentYRange[0]);

          // Save history once per double-click
          saveHistory();

          // Gentle zoom-in: 1.2x (same factor on both axes to preserve aspect ratio)
          const scaleFactor = 1 / 1.2;
          const xr = xRangeRef.current;
          const yr = yRangeRef.current;

          const newXRange = [
            centerX - (centerX - xr[0]) * scaleFactor,
            centerX + (xr[1] - centerX) * scaleFactor
          ];
          const newYRange = [
            centerY - (centerY - yr[0]) * scaleFactor,
            centerY + (yr[1] - centerY) * scaleFactor
          ];

          setXrange(newXRange);
          setYrange(newYRange);

          if (canvasEl._useWebGL) {
            prevRangesRef.current = { xRange: newXRange, yRange: newYRange };
            drawMandelbrotFractalWebGL(canvasEl, newXRange, newYRange, colorParams);
            setIsRendered(true);
          } else {
            setIsZoom(true);
            setIsRendered(false);
          }
        }}
        onMouseDown={(event) => {
          const [mouseDownX, mouseDownY] = mouseXY(event);
          setMouseDownPos([mouseDownX, mouseDownY]);
          const canvasEl = canvasElRef.current;
          if (canvasEl) {
            panStartRef.current = {
              x: mouseDownX,
              y: mouseDownY,
              // Capture ranges at drag start; used as pan base for WebGL
              xRange: [xRange[0], xRange[1]],
              yRange: [yRange[0], yRange[1]],
            };
            historySavedForPanRef.current = false;
          }
        }}
        onMouseUp={(event) => {
          const [mouseUpX, mouseUpY] = mouseXY(event);
          const [mouseDownX, mouseDownY] = mouseDownPos;
          const start = panStartRef.current;
          const wasWebGLPan = canvasElRef.current?._useWebGL && start != null;
          panStartRef.current = null;
          historySavedForPanRef.current = false;
          if (rafPanRef.current != null) {
            cancelAnimationFrame(rafPanRef.current);
            rafPanRef.current = null;
          }
          // If this mouseup is part of a double-click, let the onDoubleClick handler
          // manage zooming and skip click-selection / pan logic.
          if (event.detail && event.detail > 1) {
            return;
          }
          if (mouseUpX === mouseDownX && mouseUpY === mouseDownY) {
            // Simple click: do not change the viewport. Zoom is handled by
            // wheel / pinch / double-click only.
            return;
          }
          if (wasWebGLPan) {
            // Commit final ranges to React state after WebGL drag
            const deltaX = start.x - mouseUpX;
            const deltaY = start.y - mouseUpY;
            const finalXRange = [start.xRange[0] + deltaX, start.xRange[1] + deltaX];
            const finalYRange = [start.yRange[0] + deltaY, start.yRange[1] + deltaY];
            setXrange(finalXRange);
            setYrange(finalYRange);
            prevRangesRef.current = { xRange: finalXRange, yRange: finalYRange };
            setIsRendered(true);
            return;
          }
          saveHistory();
          const xPan = mouseDownX - mouseUpX;
          const yPan = mouseDownY - mouseUpY;
          setIsPan(true);
          setXrange([xRange[0] + xPan, xRange[1] + xPan]);
          setYrange([yRange[0] + yPan, yRange[1] + yPan]);
          setIsRendered(false);
        }}
        onMouseMove={(event) => {
          const [mouseX, mouseY] = mouseXY(event);
          const formatCoord = (n) => {
            const sign = n >= 0 ? ' ' : '';
            return sign + n.toFixed(8);
          };
          setMousePosX(formatCoord(mouseX));
          setMousePosY(formatCoord(mouseY));

          const start = panStartRef.current;
          const canvasEl = canvasElRef.current;
          if (!start || !canvasEl?._useWebGL) return;

          const deltaX = start.x - mouseX;
          const deltaY = start.y - mouseY;
          const newXRange = [start.xRange[0] + deltaX, start.xRange[1] + deltaX];
          const newYRange = [start.yRange[0] + deltaY, start.yRange[1] + deltaY];

          // Throttle to one WebGL draw per animation frame; always use latest delta
          // to avoid jitter while dragging.
          canvasEl._pendingPanRange = { xRange: newXRange, yRange: newYRange };
          if (rafPanRef.current != null) return;
          rafPanRef.current = requestAnimationFrame(() => {
            rafPanRef.current = null;
            if (!historySavedForPanRef.current) {
              saveHistory();
              historySavedForPanRef.current = true;
            }
            const pending = canvasEl._pendingPanRange;
            if (!pending) return;
            const { xRange: xr, yRange: yr } = pending;
            prevRangesRef.current = { xRange: xr, yRange: yr };
            drawMandelbrotFractalWebGL(canvasEl, xr, yr, colorParams);
          });
        }}
        onMouseLeave={() => {
          setMousePosX(null);
          setMousePosY(null);
          if (panStartRef.current != null) {
            const wasWebGLPan = canvasElRef.current?._useWebGL;
            if (wasWebGLPan) {
              // Commit current pan so next mousedown uses correct range (avoids sudden shift)
              const { xRange: xr, yRange: yr } = prevRangesRef.current;
              setXrange(xr);
              setYrange(yr);
              setIsRendered(true);
            }
            panStartRef.current = null;
            historySavedForPanRef.current = false;
            if (rafPanRef.current != null) {
              cancelAnimationFrame(rafPanRef.current);
              rafPanRef.current = null;
            }
          }
        }}
        style={{ cursor: isShiftPressed ? 'zoom-in' : 'move' }}>
      </canvas>
    </div>
    <figcaption style={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
      <div className="figcaption-inner">
        <div className="figcaption-ranges">
          {(() => {
            const fmt = (n) => (n >= 0 ? ' ' : '') + n.toFixed(8);
            return (
              <>
                x [{fmt(xRange[0])}, {fmt(xRange[1])}]<br/>
                y [{fmt(yRange[0])}, {fmt(yRange[1])}]<br/>
                {(mousePosX && mousePosY) ? `(${mousePosX}, ${mousePosY})` : ' '}
              </>
            );
          })()}
        </div>
        {showRendererToggle && (
          <div className="renderer-toggle">
            Renderer: {renderBackend || 'Detecting…'}
          </div>
        )}
      </div>
    </figcaption>
  </figure>;
}
